import type { Chart, ChartTypeRegistry, TooltipOptions } from 'chart.js';

interface CustomTooltipConfig {
  className?: string;
  minWidth?: string;
  maxWidth?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: string;
  padding?: string;
  fontSize?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
  boxShadow?: string;
  titleFontSize?: string;
  titleColor?: string;
  valueFontWeight?: string;
  valueColor?: string;
  customData?: string[];
}

const defaultConfig: Required<CustomTooltipConfig> = {
  className: 'custom-chart-tooltip',
  minWidth: '200px',
  maxWidth: '300px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  textColor: 'white',
  borderRadius: '8px',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  textAlign: 'center',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  titleFontSize: '13px',
  titleColor: '#e5e7eb',
  valueFontWeight: 'bold',
  valueColor: '#ffffff',
  customData: [],
};

const getOrCreateCustomTooltip = (chart: Chart, config: Required<CustomTooltipConfig>) => {
  let tooltipEl = chart.canvas.parentNode?.querySelector(`div.${config.className}`) as HTMLElement;

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = config.className;
    tooltipEl.style.background = config.backgroundColor;
    tooltipEl.style.borderRadius = config.borderRadius;
    tooltipEl.style.color = config.textColor;
    tooltipEl.style.opacity = '1';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'top .2s ease, left .2s ease';
    tooltipEl.style.padding = config.padding;
    tooltipEl.style.fontSize = config.fontSize;
    tooltipEl.style.fontFamily = config.fontFamily;
    tooltipEl.style.lineHeight = '1.4';
    tooltipEl.style.whiteSpace = 'nowrap';
    tooltipEl.style.minWidth = config.minWidth;
    tooltipEl.style.maxWidth = config.maxWidth;
    tooltipEl.style.textAlign = config.textAlign;
    tooltipEl.style.boxShadow = config.boxShadow;

    const table = document.createElement('table');
    table.style.margin = '0px';
    table.style.width = '100%';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode?.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const createCustomTooltipHandler = (config: Required<CustomTooltipConfig>) => (context: any) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateCustomTooltip(chart, config);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const dataPoints = tooltip.dataPoints || [];

    const tableHead = document.createElement('thead');

    titleLines.forEach((title: string) => {
      const tr = document.createElement('tr') as HTMLTableRowElement;
      tr.style.borderWidth = '0';

      const th = document.createElement('th');
      th.style.borderWidth = '0';
      th.style.fontWeight = 'normal';
      th.style.fontSize = config.titleFontSize;
      th.style.color = config.titleColor;
      th.style.paddingBottom = '8px';

      const text = document.createTextNode(title);

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    const tableBody = document.createElement('tbody');

    dataPoints.forEach((dataPoint: any, i: number) => {
      const colors = tooltip.labelColors[i];
      const dataIndex = dataPoint.dataIndex;

      // Use custom data if provided, otherwise use default formatting
      let displayText = `${dataPoint.formattedValue}`;

      if (config.customData?.[dataIndex]) {
        displayText = config.customData[dataIndex];
      }

      const span = document.createElement('span');
      span.style.background = colors.backgroundColor as string;
      span.style.borderColor = colors.borderColor as string;
      span.style.borderWidth = '2px';
      span.style.marginRight = '8px';
      span.style.height = '12px';
      span.style.width = '12px';
      span.style.display = 'inline-block';
      span.style.borderRadius = '50%';
      span.style.verticalAlign = 'middle';

      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = '0';

      const td = document.createElement('td');
      td.style.borderWidth = '0';
      td.style.fontSize = config.fontSize;
      td.style.fontWeight = config.valueFontWeight;
      td.style.color = config.valueColor;
      td.style.textAlign = config.textAlign;
      td.style.lineHeight = '1.6';

      const text = document.createTextNode(displayText);

      td.appendChild(span);
      td.appendChild(text);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot?.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot?.appendChild(tableHead);
    tableRoot?.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
  tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
  tooltipEl.style.font = (tooltip.options.bodyFont as any).string;
};

export const createCustomTooltipPlugin = (userConfig?: CustomTooltipConfig): Partial<TooltipOptions<keyof ChartTypeRegistry>> => {
  const config = { ...defaultConfig, ...userConfig };

  return {
    enabled: false,
    position: 'nearest',
    external: createCustomTooltipHandler(config),
  };
};
